import {ExtraCharge, PaymentStatus, UtilityRecord} from "../types/utilityRecord";
import {Room} from "../types/room";

/**
 * Tổng các khoản thu thêm của một tháng - ĐÃ nằm sẵn trong `record.total`, hàm
 * này chỉ dùng khi cần tách chúng ra khỏi tiền phòng/điện/nước/rác.
 *
 * Ở helpers vì utils/statistics.ts cần nó, mà module đó cố ý không được kéo
 * theo Firebase (xem chú thích đầu file).
 */
export const sumExtras = (extras?: ExtraCharge[] | null): number =>
    (extras || []).reduce((sum, item) => sum + (Number(item?.amount) || 0), 0);

export const getCurrentYearMonth = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
};

export const formatCurrency = (amount: number): string => {
    try {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    } catch (error) {
        console.error('Error formatting currency:', error);
        return `${amount.toLocaleString('vi-VN')} đ`;
    }
};

/**
 * Format year-month string from YYYY-MM to MM/YYYY format
 * @param yearMonth - Year-month string in YYYY-MM format
 * @returns Formatted string in MM/YYYY format
 */
export const formatYearMonth = (yearMonth: string): string => {
    return yearMonth.replace(/(\d{4})-(\d{2})/, '$2/$1');
};

// Dịch chuỗi YYYY-MM đi `delta` tháng (âm = lùi về quá khứ)
export const shiftYearMonth = (yearMonth: string, delta: number): string => {
    const [year, month] = yearMonth.split('-').map(Number);
    const date = new Date(year, month - 1 + delta, 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

export const getMeterReadingCurrent = (
    record: UtilityRecord | null,
    type: 'electricity' | 'water',
    index: number
): number => {
    if (!record) return 0;
    const readings = record[type];
    if (!readings) return 0;

    const reading = readings.find(r => r.index === index);
    return reading?.current ?? 0;
};

/**
 * Format date string to MM/DD/YYYY format
 * @param dateString - Date string in any valid format
 * @returns Formatted date string in MM/DD/YYYY format
 */
export const formatDateDMY = (dateString: string): string => {
    const date = new Date(dateString);

    // Kiểm tra xem date có hợp lệ không
    if (isNaN(date.getTime())) {
        return '';
    }

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
};

/**
 * Nội dung chuyển khoản, vd "P201 8.2026".
 *
 * Người thuê phải GÕ TAY chuỗi này vào app ngân hàng, mà nhiều app còn cắt bớt
 * nội dung dài - nên rút gọn hết mức: bỏ chữ "Phòng", bỏ số 0 ở đầu tháng.
 * Vẫn đủ để chủ nhà đối chiếu sổ phụ: phòng nào, tháng nào.
 */
export const buildTransferNote = (roomId: string, yearMonth: string): string => {
    const [year, month] = yearMonth.split('-');
    return `P${roomId} ${Number(month)}.${year}`;
};

/**
 * Hóa đơn đã CHỐT hay còn TẠM TÍNH.
 *
 * Chốt = chủ nhà đã duyệt số điện nước, tổng tiền không đổi nữa nên người thuê
 * chuyển khoản theo được. Trước lúc đó (người thuê vừa gửi số, hoặc số bị từ
 * chối) mọi con số chỉ là tạm tính - hóa đơn phải nói rõ điều đó và KHÔNG mời
 * chuyển khoản, kể cả khi đã bị tải/chia sẻ thành ảnh đi nơi khác.
 *
 * Định nghĩa nằm một chỗ vì cùng lúc bốn nơi hỏi câu này: nút xem hóa đơn bên
 * người thuê, nút hóa đơn ở thẻ phòng, thân hóa đơn, và ảnh chụp hàng loạt -
 * lệch nhau một chỗ là gửi cho người thuê tờ hóa đơn tạm mà không có dấu.
 */
const FINAL_INVOICE_STATUSES: PaymentStatus[] = [
    'waiting_payment', 'payment_submitted', 'paid', 'deducted_deposit',
];

export const isFinalInvoice = (record: UtilityRecord | null | undefined): boolean =>
    !!record && FINAL_INVOICE_STATUSES.includes(record.paymentStatus);

export const isProvisionalInvoice = (record: UtilityRecord | null | undefined): boolean =>
    !!record && !isFinalInvoice(record);

/**
 * "Xong tiền" - đã nhận chuyển khoản HOẶC đã trừ vào cọc lúc trả phòng.
 *
 * Một chỗ duy nhất trả lời câu này, vì nó được hỏi ở tám nơi (doanh thu, công
 * nợ, chấm trạng thái thẻ phòng, nhóm của /diennuoc, thanh tiến độ, lịch sử bên
 * người thuê, chuông báo, quyết toán cọc). Trước đây mỗi nơi tự viết
 * `=== 'paid'`, nên thêm một trạng thái kết thúc là phải đi lùng đủ tám chỗ -
 * sót một chỗ thì phòng đã trả xong vẫn hiện "chưa thu" ở đúng chỗ đó.
 */
export const SETTLED_STATUSES: PaymentStatus[] = ['paid', 'deducted_deposit'];

export const isSettled = (record: UtilityRecord | null | undefined): boolean =>
    !!record && SETTLED_STATUSES.includes(record.paymentStatus);

export const isFutureMonth = (yearMonth: string): boolean => {
    const currentDate = new Date();
    const [year, month] = yearMonth.split('-').map(Number);
    const inputDate = new Date(year, month - 1); // month - 1 vì tháng trong JS bắt đầu từ 0

    // Set ngày về 1 để so sánh chỉ tháng và năm
    currentDate.setDate(1);
    currentDate.setHours(0, 0, 0, 0);

    return inputDate > currentDate;
};

/**
 * Phòng này tháng nào cũng phải nhập số / chốt tiền hay không.
 *
 * Không đồng hồ, không tiền phòng thì chẳng có gì để nhập lẫn để thu - phòng
 * người nhà ở chẳng hạn. Để nó trong danh sách điện nước thì tháng nào nó cũng
 * nằm ở nhóm "Chưa nhập" và thanh tiến độ không bao giờ đủ 100%.
 * Tiêu chí đọc thẳng từ hồ sơ phòng, chủ nhà tự chỉnh được, không cần cờ riêng.
 *
 * Cố ý KHÔNG xét garbagePrice: giá rác là giá mặc định đóng dấu lên mọi phòng
 * lúc tạo, phòng nào cũng có nên nó không phân biệt được phòng nào cần thu.
 */
export const needsUtilityEntry = (room: Room): boolean =>
    (room.electricMeterCount ?? 0) > 0
    || (room.waterMeterCount ?? 0) > 0
    || (room.basePrices?.roomPrice ?? 0) > 0;

export const sortRoomsByName = (rooms: [string, Room][]): [string, Room][] => {
    return [...rooms].sort((a, b) => {
        const roomNameA = a[1].name;
        const roomNameB = b[1].name;

        // Tách phần text và số từ tên phòng
        const [, textA = "", numberA = ""] = roomNameA.match(/^(.*?)(\d+)(?:\s*-\s*\d+)?$/) || ["", "", ""];
        const [, textB = "", numberB = ""] = roomNameB.match(/^(.*?)(\d+)(?:\s*-\s*\d+)?$/) || ["", "", ""];

        // So sánh phần text trước
        const textComparison = textA.localeCompare(textB);
        if (textComparison !== 0) return textComparison;

        // Nếu phần text giống nhau, so sánh phần số dựa trên giá trị số nguyên 
        return parseInt(numberA || "0", 10) - parseInt(numberB || "0", 10);
    });
};